/**
 * Client side of the publish gate (DC-10, DC-21, AC-026).
 * The attribution finding is computed HERE from the placement config + the project's
 * enabled integrations — the server is never asked for it.
 *
 * @module webplacements/services
 */
import type { PublishGateFinding } from '@modules/shared-types/src/launchActivation.types';
import type { AnalyticsIntegration } from '@modules/analytics-settings/types/analytics.types';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import { REQUIRED_CONFIG_FIELDS } from '@modules/analytics-settings/config/requiredConfigFields';
import {
  ATTRIBUTION_NETWORK_LABELS,
  ATTRIBUTION_PRESETS,
  defaultToggles,
  deriveToggleState,
  type AttributionConfigSlice,
  type AttributionPresetKey,
} from '@modules/webplacements/config/attributionPresets';

export const MISSING_ATTRIBUTION_ID = 'missing_attribution' as const;
const NETWORKS: readonly AttributionPresetKey[] = ['facebook', 'tiktok'];
const SEVERITY_ORDER: Record<PublishGateFinding['severity'], number> = { error: 0, warning: 1, not_evaluated: 2 };

/**
 * Row identity here MUST match `deriveToggleState` — key AND source (MIN-14). A row with the
 * right key but the wrong source does not satisfy the preset, so it must still be listed as
 * missing; otherwise the finding names a toggle as off while claiming nothing is missing.
 */
const hasPresetRow = (rows: readonly ParameterDefinition[], preset: ParameterDefinition): boolean =>
  rows.some((row) => row.key === preset.key && row.source === preset.source);

function missingRows(slice: AttributionConfigSlice, key: AttributionPresetKey): { url: string[]; server: string[] } {
  const preset = ATTRIBUTION_PRESETS[key];
  const url = slice.urlParams ?? [];
  const server = slice.serverData ?? [];
  return {
    url: preset.urlParams.filter((p) => !hasPresetRow(url, p)).map((p) => p.key),
    server: preset.serverData.filter((p) => !hasPresetRow(server, p)).map((p) => p.key),
  };
}

/**
 * Client-owned `missing_attribution` finding (DC-10): a network whose pixel integration
 * is enabled AND configured but whose toggle is OFF on this placement.
 * Never computed server-side — `mergeGateFindings` drops any server copy.
 */
export function buildMissingAttributionFinding(
  config: AttributionConfigSlice | undefined,
  integrations: AnalyticsIntegration[],
  variantLabel?: string,
): PublishGateFinding | null {
  const slice: AttributionConfigSlice = config ?? { urlParams: [], serverData: [], stripeMappings: [] };
  const wanted = defaultToggles(integrations, REQUIRED_CONFIG_FIELDS);
  const state = deriveToggleState(slice);
  const missing = NETWORKS.filter((k) => wanted[k] && !state[k]);
  if (missing.length === 0) return null;

  const names = missing.map((k) => ATTRIBUTION_NETWORK_LABELS[k]);
  const detail = missing.map((k) => {
    const rows = missingRows(slice, k);
    const parts = [
      rows.url.length ? `URL parameters: ${rows.url.join(', ')}` : null,
      rows.server.length ? `server rows: ${rows.server.join(', ')}` : null,
    ].filter(Boolean);
    return `${ATTRIBUTION_NETWORK_LABELS[k]} — missing ${parts.join('; ') || 'Stripe metadata mappings'}.`;
  }).join(' ');

  return {
    id: MISSING_ATTRIBUTION_ID,
    severity: 'warning',
    title: `${names.join(' and ')} attribution is off${variantLabel ? ` (${variantLabel})` : ''}`,
    detail: `The ${names.join(' / ')} pixel is enabled for this project, but this placement does not capture its attribution. ${detail} Turn the toggle on in step 2.`,
    actions: [{ class: 'D', label: `← Step 2 · ${names[0]} toggle`, articleAlias: 'webplacement-stripe-metadata' }],
  };
}

/**
 * Merge server findings with the client-computed attribution finding.
 * Drops any server `missing_attribution` (DC-10 — client owns it), dedupes by
 * `id+screenId+title`, orders error → warning → not_evaluated.
 */
export function mergeGateFindings(server: PublishGateFinding[] | undefined, client: PublishGateFinding[]): PublishGateFinding[] {
  const seen = new Set<string>();
  const all = [...(server ?? []).filter((f) => f.id !== MISSING_ATTRIBUTION_ID), ...client].filter((f) => {
    const key = `${f.id}|${f.screenId ?? ''}|${f.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return all.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/** AM-3: only `severity === 'error'` pauses the deploy — never `healthcheck.errors`. */
export function hasBlockingFinding(findings: PublishGateFinding[]): boolean {
  return findings.some((f) => f.severity === 'error');
}
